/*
 * Farida Gupta - "Load more" for every fg-blog-tabs panel (sections/fg-blog-tabs.liquid,
 * snippets/fg-blog-tabs-panel.liquid) and the account page's order list
 * (sections/customer-account-tabs.liquid).
 *
 * Unlike fg-load-more.js, this never fetches anything. Each tab filters the section's blog down by
 * article tag, and tags aren't a native {% paginate %} filter, so the panel already rendered every
 * matching card it can show, up to a bounded cap, with everything past the first page marked
 * `hidden`. This just reveals the next `batch-size` hidden cards per click and removes itself once
 * none are left.
 *
 * `list-id` accepts several space-separated ids. The order list needs this: it renders the same
 * orders twice (a mobile card list and a desktop table, toggled by CSS breakpoint, not JS), and one
 * click has to reveal the next batch in both at once so the hidden view doesn't fall behind and
 * show a stale count if the viewport is resized past the breakpoint later.
 *
 * Reveals a hidden batch of already-rendered cards, which is instant - there's no fetch to wait on.
 * A brief aria-busy pause (with the same .collection__load-more-loader spinner fg-load-more.js
 * uses) is purely cosmetic: without it, a click would disable the button for a single frame with
 * nothing for the visitor to see, which reads as an unresponsive click rather than a load.
 */
const REVEAL_DELAY_MS = 300;

class RevealMoreButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => this.revealMore());
  }

  getLists() {
    const ids = (this.getAttribute('list-id') || '').split(/\s+/).filter(Boolean);

    return ids.map((id) => document.getElementById(id)).filter(Boolean);
  }

  async revealMore() {
    if (!this.button || this.button.disabled) return;

    const lists = this.getLists();
    const batchSize = Number(this.getAttribute('batch-size'));

    if (lists.length === 0 || !batchSize) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      await new Promise((resolve) => setTimeout(resolve, REVEAL_DELAY_MS));

      let remainingHidden = 0;

      lists.forEach((list) => {
        const hiddenCards = [...list.children].filter((child) => child.hidden);

        hiddenCards.slice(0, batchSize).forEach((card) => {
          card.hidden = false;
        });

        remainingHidden = Math.max(remainingHidden, hiddenCards.length - batchSize);
      });

      if (remainingHidden <= 0) {
        this.remove();
      }
    } finally {
      if (this.button) {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }
}

if (!window.customElements.get('reveal-more-button')) {
  window.customElements.define('reveal-more-button', RevealMoreButton);
}
